import { formatGbp } from 'src/utils/formatGbp'
import { mapConversionRecordsToOptions } from 'src/utils/stockTakeConversions'

/**
 * Kitchen words for Waste. Later screens import these.
 * Stored reason ids match Express. Labels are what the chips show.
 */

export const WASTAGE_COPY = Object.freeze({
  title: 'Waste',
  line: 'Log it when you throw it away. It rolls into the next stock take.',
  record: 'Record waste',
  back: 'Go back',
  edit: 'Edit',
  remove: 'Remove',
  confirmRemove: 'Remove this waste? You can see it again from Show removed.',
  showRemoved: 'Show removed',
  removed: 'Removed',
  save: 'Save',
  cancel: 'Cancel',
  saved: 'Saved',
  undo: 'Undo',
  recordAgain: 'Record again',
  search: 'Search products or recipes',
  searchEmpty: 'Nothing matched.',
  largeQuantity: "That's a very large quantity. Save it anyway?",
  alreadyToday: "This is already on today's list.",
  alreadyThatDay: "This is already on this day's list.",
  loggedBefore: 'Logged before',
  dateToday: 'Today',
  note: 'Note',
  recipe: 'Recipe',
  portions: 'Portions',
  fullRecipe: 'Full Recipe',
  futureDay: "Waste can't be recorded on a future day",
  reversedRange: 'The start date is after the end date.',
  columnHint: 'Record waste as it happens',
  notInCatalogue: 'Not in the catalogue — type wastage here',
  wastedRecipesAction: 'Add Wasted Recipes',
  wastedRecipesSameList:
    'This is the same list as Waste. A dish logged here lands on the ingredient lines.',
  fromWaste: 'From Waste',
  fromWasteIncludingVat: 'From Waste, including VAT',
  fromWasteHint: 'Recorded in Waste',
  wasteBiggerThanOnHand: 'Waste is bigger than the stock on hand.',
  outsideDates: 'Outside these dates',
  notOnStockTake: 'Not on a stock take yet',
  openCountMissesDay:
    'This waste is waiting for a stock take that includes this day.',
  onLockedStockTake: 'On a locked stock take',
  unlockWastage:
    'Wastage figures may move. Waste recorded for this period is counted while the take is open.',
  openThisCount: 'Open this count',
  continueThisCount: 'Continue this count',
  countToday: 'Count today',
  filterAllReasons: 'All reasons',
  filterAllPeople: 'All recorded by',
})

/** Stock home card. Step 29 adds today’s cost beside this title and line. */
export const WASTAGE_STOCK_HOME_CARD = Object.freeze({
  title: WASTAGE_COPY.title,
  line: WASTAGE_COPY.line,
})

/** Empty Waste page. The one line and Record waste teach the job. */
export const WASTAGE_EMPTY = Object.freeze({
  title: WASTAGE_COPY.title,
  body: WASTAGE_COPY.line,
  action: WASTAGE_COPY.record,
})

export const WASTAGE_REASON = Object.freeze({
  SPOILAGE: 'spoilage',
  PREPARATION: 'preparation',
  OVERPRODUCTION: 'overproduction',
  DAMAGED: 'damaged',
  RETURNED: 'returned',
  STAFF_ERROR: 'staff_error',
  CUSTOMER_RETURN: 'customer_return',
  OTHER: 'other',
})

export const WASTAGE_REASON_LABELS = Object.freeze({
  [WASTAGE_REASON.SPOILAGE]: 'Spoilage / expired',
  [WASTAGE_REASON.PREPARATION]: 'Preparation waste',
  [WASTAGE_REASON.OVERPRODUCTION]: 'Overproduction',
  [WASTAGE_REASON.DAMAGED]: 'Damaged',
  [WASTAGE_REASON.RETURNED]: 'Returned / rejected',
  [WASTAGE_REASON.STAFF_ERROR]: 'Staff error',
  [WASTAGE_REASON.CUSTOMER_RETURN]: 'Customer return',
  [WASTAGE_REASON.OTHER]: 'Other',
})

/** Chip order. None is selected until the cook taps one. */
export const WASTAGE_REASON_CHIPS = Object.freeze(
  Object.values(WASTAGE_REASON).map(id =>
    Object.freeze({ id, label: WASTAGE_REASON_LABELS[id] }),
  ),
)

/** People who recorded waste in the current list, for the Recorded by filter. */
export const wastageUserOptions = (entries = []) => {
  const seen = new Set()
  const options = []
  for (const entry of entries || []) {
    if (entry?.deletedAt) continue
    const id = String(entry?.recordedBy || '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    const name = String(entry?.recordedByName || '').trim() || 'Recorded by'
    options.push({ id, name })
  }
  return options.sort((left, right) =>
    left.name.localeCompare(right.name, 'en-GB'),
  )
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const calendarParts = value => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''))
  if (!match) return null
  const monthIndex = Number(match[2]) - 1
  if (monthIndex < 0 || monthIndex > 11) return null
  return {
    year: match[1],
    month: MONTHS[monthIndex],
    day: String(Number(match[3])),
  }
}

/** "1–31 Aug" from the take’s calendar days. */
export const wastageStockTakePeriodLabel = (fromPeriod, toPeriod) => {
  const from = calendarParts(fromPeriod)
  const to = calendarParts(toPeriod)
  if (!from || !to) return ''
  if (from.year === to.year && from.month === to.month) {
    return `${from.day}–${to.day} ${from.month}`
  }
  if (from.year === to.year) {
    return `${from.day} ${from.month}–${to.day} ${to.month}`
  }
  return `${from.day} ${from.month} ${from.year}–${to.day} ${to.month} ${to.year}`
}

/** Calendar day for “includes 6 Oct”, never “Today”. */
export const wastageIncludedDayLabel = businessDate => {
  const parts = calendarParts(businessDate)
  if (!parts) return ''
  return `${parts.day} ${parts.month}`
}

/**
 * An unlocked count exists, and this day is not inside it.
 * Names the waste day, not the open count, so it does not read as an error.
 */
export const wastageOpenCountLine = (openStockTake, businessDate) => {
  if (!openStockTake) return WASTAGE_COPY.openCountMissesDay
  const day = wastageIncludedDayLabel(businessDate)
  if (!day) return WASTAGE_COPY.openCountMissesDay
  return `This waste is waiting for a stock take that includes ${day}.`
}

const takeEndDay = stockTake =>
  wastageCalendarDay(stockTake?.toPeriod) ||
  wastageCalendarDay(stockTake?.fromPeriod)

/**
 * Last locked take that ended on or before this take.
 * Prefers a Main lock (official wastage), else any locked take.
 */
export const previousLockedStockTake = (stockTake, allTakes = []) => {
  const end = takeEndDay(stockTake)
  const id = String(stockTake?._id || '')
  const locked = (Array.isArray(allTakes) ? allTakes : []).filter(other => {
    if (!other?.isLocked || String(other._id || '') === id) return false
    const day = takeEndDay(other)
    return Boolean(day && (!end || day <= end))
  })
  const mains = locked.filter(
    other => String(other.countType || 'MAIN').toUpperCase() === 'MAIN',
  )
  const pool = mains.length ? mains : locked
  return (
    [...pool].sort((left, right) =>
      String(takeEndDay(right)).localeCompare(String(takeEndDay(left))),
    )[0] || null
  )
}

/**
 * Open take wastage: previous locked take → this take.
 * Locked take: its own from–to. Missing dates stay empty (never today–today).
 */
export const wastageReportDateRange = (stockTake, allTakes = []) => {
  const thisEnd = takeEndDay(stockTake)
  const thisStart = wastageCalendarDay(stockTake?.fromPeriod) || thisEnd
  if (!thisEnd) return { from: null, to: null }
  if (stockTake?.isLocked) return { from: thisStart, to: thisEnd }
  const previous = previousLockedStockTake(stockTake, allTakes)
  const prevDay = takeEndDay(previous)
  return { from: prevDay || thisStart, to: thisEnd }
}

export const formatWastageReportDateHeading = range => {
  const from = calendarParts(range?.from)
  const to = calendarParts(range?.to)
  if (!from || !to) return ''
  const label = parts =>
    `${String(parts.day).padStart(2, '0')} ${parts.month} ${parts.year}`
  const start = label(from)
  const end = label(to)
  return start === end ? start : `${start} - ${end}`
}

/**
 * Where this entry will land.
 * A locked take uses its own sentence. A day outside the open count waits
 * for a take that includes that day. No count at all stays "Not on a stock take yet".
 * @param {{ fromPeriod?: string, toPeriod?: string, isLocked?: boolean } | null} stockTake
 * @param {{ fromPeriod?: string, toPeriod?: string } | null} [openStockTake]
 */
export const wastageStockTakeLine = (
  stockTake,
  openStockTake,
  businessDate,
) => {
  if (!stockTake) {
    if (openStockTake) return wastageOpenCountLine(openStockTake, businessDate)
    return WASTAGE_COPY.notOnStockTake
  }
  if (stockTake.isLocked) return WASTAGE_COPY.onLockedStockTake
  const period = wastageStockTakePeriodLabel(
    stockTake.fromPeriod,
    stockTake.toPeriod,
  )
  if (!period) return WASTAGE_COPY.notOnStockTake
  return `On the ${period} stock take`
}

const wastageEntryKey = entry => String(entry?._id || entry?.id || '')

/**
 * The dates on screen stay as they are. A row just saved on another day
 * still shows, until that day is inside the range or the list already has it.
 * That extra row is marked outsideRange so the list can say so.
 */
export const wastageListWithJustSaved = (
  entries = [],
  saved = [],
  { from = '', to = '', productId = '' } = {},
) => {
  const listed = (Array.isArray(entries) ? entries : []).filter(Boolean)
  const known = new Set(listed.map(wastageEntryKey))
  const extra = (Array.isArray(saved) ? saved : [])
    .filter(entry => {
      const id = wastageEntryKey(entry)
      const day = entry?.businessDate
      if (!id || !day || entry.deletedAt || known.has(id)) return false
      if (from && to && day >= from && day <= to) return false
      if (
        productId &&
        String(entry.venueProductId || entry.recipeId || '') !==
          String(productId)
      ) {
        return false
      }
      return true
    })
    .map(entry => {
      const day = entry.businessDate
      const outside = Boolean(from && to && (day < from || day > to))
      return outside ? { ...entry, outsideRange: true } : entry
    })
  return [...listed, ...extra].sort((left, right) => {
    const byDate = String(right.businessDate || '').localeCompare(
      String(left.businessDate || ''),
    )
    if (byDate) return byDate
    return String(right.recordedAt || '').localeCompare(
      String(left.recordedAt || ''),
    )
  })
}

/**
 * "About £12.50" when a snapshot cost exists. Unknown or zero stays blank.
 * @param {number | null | undefined} costValuePence
 * @returns {string | null}
 */
export const wastageAboutCost = costValuePence => {
  const pence = Number(costValuePence)
  if (!Number.isFinite(pence) || pence <= 0) return null
  return `About ${formatGbp(pence / 100)}`
}

/** Kitchen day in London, YYYY-MM-DD. */
export const wastageLondonToday = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)

/** Clock time in London, e.g. "12 Aug 2026, 19:00". */
export const wastageFormatUkDateTime = value => {
  if (value == null || value === '') return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const pick = type => parts.find(part => part.type === type)?.value || ''
  const day = pick('day')
  const month = pick('month')
  const year = pick('year')
  const hour = pick('hour')
  const minute = pick('minute')
  if (!day || !month || !year || hour === '' || minute === '') return ''
  return `${day} ${month} ${year}, ${hour}:${minute}`
}

export const wastageLatestEdit = (audit = []) => {
  const edits = (Array.isArray(audit) ? audit : []).filter(
    event => String(event?.action || '') === 'edit',
  )
  if (!edits.length) return null
  return edits.reduce((latest, event) => {
    const at = new Date(event.at).getTime()
    const latestAt = new Date(latest.at).getTime()
    if (!Number.isFinite(at)) return latest
    if (!Number.isFinite(latestAt) || at >= latestAt) return event
    return latest
  })
}

export const wastageRecordedByLine = entry => {
  const name = String(entry?.recordedByName || '').trim()
  const when = wastageFormatUkDateTime(entry?.recordedAt)
  if (!name && !when) return ''
  if (name && when) return `Recorded by ${name} · ${when}`
  if (name) return `Recorded by ${name}`
  return `Recorded ${when}`
}

export const wastageLastEditedLine = entry => {
  const edit = wastageLatestEdit(entry?.audit)
  if (!edit) return ''
  const name = String(edit.byName || '').trim()
  const when = wastageFormatUkDateTime(edit.at)
  if (!name && !when) return ''
  if (name && when) return `Last edited by ${name} · ${when}`
  if (name) return `Last edited by ${name}`
  return `Last edited ${when}`
}

/**
 * A date-only string stays as written. An instant becomes the London day.
 * @param {Date|string|number|null|undefined} value
 * @returns {string|null}
 */
export const wastageCalendarDay = value => {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
    value = trimmed
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return wastageLondonToday(date)
}

/**
 * The kitchen day a stock-take action should record.
 * Today, when it sits inside the take. Otherwise the last day of the take.
 */
export const wastageBusinessDateForStockTake = (stockTake, today) => {
  const day = wastageCalendarDay(today) || wastageLondonToday()
  const from = wastageCalendarDay(stockTake?.fromPeriod)
  const to = wastageCalendarDay(stockTake?.toPeriod)
  if (!from || !to || from > to) return day
  if (day >= from && day <= to) return day
  if (to < day) return to
  return day
}

/** from and to from a Waste link, or null when the period is missing or reversed. */
export const wastageRangeFromSearch = search => {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
  const from = wastageCalendarDay(params.get('from'))
  const to = wastageCalendarDay(params.get('to'))
  if (!from || !to || from > to) return null
  return { from, to }
}

/** Venue product id from a Waste link opened from a stock take row. */
export const wastageProductFromSearch = search => {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
  return String(params.get('product') || '').trim()
}

/** Stock list for this venue. Waste returns here when it was not opened from a count. */
export const stockTakesListPath = venueId => `/v/${venueId}/stockTakes`

/**
 * Where Go back on Waste returns.
 * A count or the stock list for this venue. Any other page, or none, is the list.
 */
export const wastageBackPath = (venueId, from) => {
  const list = stockTakesListPath(venueId)
  const path = String(from || '')
    .split('?')[0]
    .replace(/\/+$/, '')
  if (path === list) return list
  const prefix = `${list}/`
  if (path.startsWith(prefix)) {
    const id = path.slice(prefix.length)
    if (id && !id.includes('/')) return path
  }
  return list
}

/** The count when one is open, otherwise the stock list. */
export const wastageBackTarget = (venueId, stockTakeId) => {
  const id = String(stockTakeId || '').trim()
  if (!id) return stockTakesListPath(venueId)
  return `${stockTakesListPath(venueId)}/${id}`
}

/** A Waste link that remembers the stock page it was opened from. */
export const wastageLinkTo = (href, backTo) => {
  const value = String(href || '')
  const queryAt = value.indexOf('?')
  return {
    pathname: queryAt === -1 ? value : value.slice(0, queryAt),
    search: queryAt === -1 ? '' : value.slice(queryAt),
    state: { backTo: String(backTo || '') },
  }
}

/** Record waste for one product. The form opens with that product selected. */
export const stockWastageRecordHref = (venueId, venueProductId) => {
  const path = `/v/${venueId}/stock/wastage`
  const id = String(venueProductId || '').trim()
  if (!id) return path
  return `${path}?product=${encodeURIComponent(id)}`
}

/** Waste for this stock take’s period. A missing range still opens Waste. */
export const stockWastagePeriodHref = (venueId, stockTake) => {
  const path = `/v/${venueId}/stock/wastage`
  const from = wastageCalendarDay(stockTake?.fromPeriod)
  const to = wastageCalendarDay(stockTake?.toPeriod)
  if (!from || !to || from > to) return path
  return `${path}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
}

/**
 * The month the Waste history opens on, in London.
 * A YYYY-MM-DD string is used as that kitchen day.
 */
export const wastageLondonMonthRange = (now = new Date()) => {
  const day =
    typeof now === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(now)
      ? now
      : wastageLondonToday(now)
  const [year, month] = day.split('-')
  const last = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate()
  return {
    from: `${year}-${month}-01`,
    to: `${year}-${month}-${String(last).padStart(2, '0')}`,
  }
}

/** The 30 London days ending on this kitchen day, inclusive. */
export const wastageRecentProductRange = (now = new Date()) => {
  const day =
    typeof now === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(now)
      ? now
      : wastageLondonToday(now)
  const [year, month, date] = day.split('-').map(Number)
  const start = new Date(Date.UTC(year, month - 1, date))
  start.setUTCDate(start.getUTCDate() - 29)
  const from = start.toISOString().slice(0, 10)
  return { from, to: day }
}

export const WASTAGE_RECENT_PRODUCT_LIMIT = 6

/** Matches Express. A longer note is rejected on save. */
export const WASTAGE_NOTE_MAX_LENGTH = 1000

const unitKey = value =>
  String(value || '')
    .trim()
    .toLowerCase()

const MASS_ALIASES = {
  kg: ['kg', 'kgs', 'kilo', 'kilos', 'kilogram', 'kilograms'],
  g: ['g', 'gs', 'gram', 'grams'],
  lb: ['lb', 'lbs', 'pound', 'pounds'],
}

const MASS_LABEL = {
  kg: 'Kilograms',
  g: 'Grams',
  lb: 'Pounds',
}

const MASS_SHORT = {
  kg: 'kg',
  g: 'g',
  lb: 'lb',
}

const massKind = unit => {
  const key = unitKey(unit).replace(/\.$/, '')
  if (!key) return ''
  return (
    Object.keys(MASS_ALIASES).find(kind => MASS_ALIASES[kind].includes(key)) ||
    ''
  )
}

export const wastageFriendlyUnit = unit => {
  const kind = massKind(unit)
  if (kind) return MASS_LABEL[kind]
  return String(unit || '').trim()
}

const formatConversionCount = value => {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return ''
  const rounded = Math.round(n * 1000) / 1000
  return rounded.toLocaleString('en-GB', { maximumFractionDigits: 3 })
}

const knownMassFactor = (countedUnit, baseUnit) => {
  const from = massKind(countedUnit)
  const to = massKind(baseUnit)
  if (!from || !to || from === to) return null
  if (to === 'kg' && from === 'g') return 1000
  if (to === 'g' && from === 'kg') return 0.001
  if (to === 'kg' && from === 'lb') return 2.20462
  if (to === 'lb' && from === 'kg') return 1 / 2.20462
  if (to === 'g' && from === 'lb') return 453.592
  if (to === 'lb' && from === 'g') return 1 / 453.592
  return null
}

const factorsMatch = (actual, expected) => {
  if (expected == null) return true
  const n = Number(actual)
  if (!Number.isFinite(n) || n <= 0) return false
  const ratio = n / expected
  return ratio > 0.98 && ratio < 1.02
}

/** Drop same-unit / bogus mass conversions instead of showing "100 per Kg". */
export const wastageConversionIsSensible = ({
  name,
  factor,
  baseUnit,
  unit,
} = {}) => {
  const counted = String(unit || name || '').trim()
  const base = String(baseUnit || '').trim()
  const n = Number(factor)
  if (!counted || !base) return false
  if (!Number.isFinite(n) || n <= 0) return false
  if (unitKey(counted) === unitKey(base)) return false
  if (massKind(counted) && massKind(counted) === massKind(base)) return false
  return factorsMatch(n, knownMassFactor(counted, base))
}

export const wastageBaseUnitLabel = baseUnit => {
  const kind = massKind(baseUnit)
  if (kind) return MASS_LABEL[kind]
  return 'Original ordering unit'
}

/** "Grams (1 kg = 1,000 g)" — never a raw conversion factor. */
export const wastageConversionChipLabel = ({
  name,
  factor,
  baseUnit,
  unit,
} = {}) => {
  const countedRaw = String(unit || name || '').trim()
  const countedFriendly =
    wastageFriendlyUnit(String(name || '').trim()) ||
    wastageFriendlyUnit(countedRaw) ||
    'Conversion'
  const base = String(baseUnit || '').trim()
  const n = Number(factor)
  if (
    !base ||
    !Number.isFinite(n) ||
    n <= 0 ||
    !wastageConversionIsSensible({
      name,
      factor,
      baseUnit,
      unit: countedRaw,
    })
  ) {
    return countedFriendly
  }
  const baseShort = MASS_SHORT[massKind(base)] || base
  const countedShort = MASS_SHORT[massKind(countedRaw)] || countedRaw
  return `${countedFriendly} (1 ${baseShort} = ${formatConversionCount(
    n,
  )} ${countedShort})`
}

/** Name when it differs from the counted unit. Otherwise the counted unit. */
const wastageCountedUnit = (name, toUnit) => {
  const title = String(name || '').trim()
  const unit = String(toUnit || '').trim()
  if (title && unitKey(title) !== unitKey(unit)) return title
  return unit
}

/**
 * Base unit, then each conversion the stock take can already count in.
 * A remembered unit that is no longer on the product stays available.
 */
export const wastageUnitOptions = (product, last) => {
  const base = String(product?.unit || '').trim()
  const options = []
  if (base) {
    options.push({
      id: 'base',
      conversionId: '',
      unit: base,
      factor: 1,
      label: wastageBaseUnitLabel(base),
    })
  }
  for (const option of mapConversionRecordsToOptions(product?.conversions)) {
    const counted = wastageCountedUnit(
      option.conversionName,
      option.conversionUnit,
    )
    if (
      !wastageConversionIsSensible({
        name: option.conversionName,
        factor: option.conversionFactor,
        baseUnit: base,
        unit: option.conversionUnit || counted,
      })
    ) {
      continue
    }
    options.push({
      id: option.value,
      conversionId: option.value,
      unit: counted,
      factor: option.conversionFactor,
      label: wastageConversionChipLabel({
        name: option.conversionName,
        factor: option.conversionFactor,
        baseUnit: base,
        unit: option.conversionUnit || counted,
      }),
    })
  }
  const rememberedId = last?.conversionId ? String(last.conversionId) : ''
  const rememberedUnit = String(last?.inputUnit || '').trim()
  const already = options.some(
    option =>
      (rememberedId && option.conversionId === rememberedId) ||
      (!rememberedId &&
        rememberedUnit &&
        unitKey(option.unit) === unitKey(rememberedUnit)),
  )
  if (rememberedUnit && !already) {
    const factor = Number(last.conversionFactor)
    if (
      wastageConversionIsSensible({
        name: rememberedUnit,
        factor,
        baseUnit: base,
        unit: rememberedUnit,
      })
    ) {
      options.push({
        id: rememberedId || `remembered-${unitKey(rememberedUnit)}`,
        conversionId: rememberedId,
        unit: rememberedUnit,
        factor: Number.isFinite(factor) && factor > 0 ? factor : null,
        label: wastageConversionChipLabel({
          name: rememberedUnit,
          factor,
          baseUnit: base,
          unit: rememberedUnit,
        }),
      })
    }
  }
  return options
}

/** The unit to show when a product is picked. Last entry wins when it still matches. */
export const wastageSelectedUnit = (product, last) => {
  const options = wastageUnitOptions(product, last)
  if (last?.conversionId) {
    const byId = options.find(
      option => option.conversionId === String(last.conversionId),
    )
    if (byId) return byId
  }
  if (last?.inputUnit) {
    const matches = options.filter(
      option => unitKey(option.unit) === unitKey(last.inputUnit),
    )
    if (matches.length === 1) return matches[0]
  }
  return (
    options[0] || {
      id: 'base',
      conversionId: '',
      unit: '',
      factor: 1,
      label: '',
    }
  )
}

const wastageEntryIsRecipe = entry =>
  entry?.kind === 'recipe' || Boolean(entry?.recipeId && !entry?.venueProductId)

/**
 * Another active row for this product or dish on this day.
 * A removed row does not count. The row being edited does not count.
 */
export const wastageAlreadyOnDay = (
  entries,
  { kind, id, businessDate, ignoreId } = {},
) => {
  const day = String(businessDate || '').trim()
  const itemId = String(id || '').trim()
  if (!day || !itemId) return false
  const recipe = kind === 'recipe'
  const skip = String(ignoreId || '')
  return (entries || []).some(entry => {
    if (!entry || entry.deletedAt) return false
    if (String(entry.businessDate || '') !== day) return false
    const entryId = String(entry._id || entry.id || '')
    if (skip && entryId === skip) return false
    if (recipe) return String(entry.recipeId || '') === itemId
    return (
      String(entry.venueProductId || '') === itemId &&
      !wastageEntryIsRecipe(entry)
    )
  })
}

/**
 * A previous log wins. A product or dish with no history keeps the reason
 * already chosen, so a run of the same reason is one tap.
 */
export const wastageKeptReason = (lastReason, chosenReason) =>
  String(lastReason || '').trim() || String(chosenReason || '').trim()

/**
 * Newest entry for each product. The list is already newest first.
 * A product with no entry is absent. Recipes are left out.
 */
export const wastageLastEntryByProduct = (entries = []) => {
  const map = new Map()
  for (const entry of entries) {
    const id = entry?.venueProductId ? String(entry.venueProductId) : ''
    if (!id || map.has(id)) continue
    const reason = WASTAGE_REASON_CHIPS.some(chip => chip.id === entry.reason)
      ? entry.reason
      : ''
    map.set(id, {
      reason,
      inputUnit: entry.inputUnit || entry.baseUoM || '',
      conversionId: entry.conversionId ? String(entry.conversionId) : '',
      conversionFactor:
        entry.conversionFactor == null ? null : Number(entry.conversionFactor),
    })
  }
  return map
}

/** Newest recipe entry. The list is already newest first. */
export const wastageLastEntryByRecipe = (entries = []) => {
  const map = new Map()
  for (const entry of entries) {
    const id = entry?.recipeId ? String(entry.recipeId) : ''
    if (!id || map.has(id)) continue
    const reason = WASTAGE_REASON_CHIPS.some(chip => chip.id === entry.reason)
      ? entry.reason
      : ''
    map.set(id, {
      reason,
      inputUnit: wastageRecipeUnit(entry),
    })
  }
  return map
}

/**
 * Products and recipes logged most often, so a repeat is one tap.
 * Ties fall back to name.
 * @param {object[]} entries
 * @param {number} [limit]
 */
export const wastageFrequentProducts = (
  entries = [],
  limit = WASTAGE_RECENT_PRODUCT_LIMIT,
) => {
  const counts = new Map()
  for (const entry of entries) {
    const recipe =
      entry?.kind === 'recipe' || (!entry?.venueProductId && entry?.recipeId)
    const id = recipe
      ? String(entry?.recipeId || '')
      : entry?.venueProductId
      ? String(entry.venueProductId)
      : ''
    if (!id) continue
    const key = `${recipe ? 'recipe' : 'product'}:${id}`
    const current = counts.get(key) || {
      id,
      kind: recipe ? 'recipe' : 'product',
      title: entry.title || (recipe ? 'Recipe' : 'Product'),
      supplier: String(entry.supplierTitle || '').trim(),
      unit: recipe ? '' : entry.baseUoM || '',
      count: 0,
    }
    current.count += 1
    if (entry.title) current.title = entry.title
    if (!recipe && entry.supplierTitle) {
      current.supplier = String(entry.supplierTitle).trim()
    }
    if (!recipe && entry.baseUoM) current.unit = entry.baseUoM
    counts.set(key, current)
  }
  return [...counts.values()]
    .sort(
      (left, right) =>
        right.count - left.count || left.title.localeCompare(right.title),
    )
    .slice(0, limit)
}

/**
 * Preview cost in pence. Venue product prices from the list are pounds.
 * Unknown or zero prices stay blank. An empty quantity stays blank.
 */
const recipePortionChoice = {
  id: 'portions',
  conversionId: '',
  unit: WASTAGE_COPY.portions,
  factor: 1,
  label: WASTAGE_COPY.portions,
}

const recipeFullChoice = {
  id: 'full',
  conversionId: '',
  unit: WASTAGE_COPY.fullRecipe,
  factor: 1,
  label: WASTAGE_COPY.fullRecipe,
}

/**
 * Portions, or the whole dish. A new recipe starts on portions.
 * When one portion costs the same as the whole dish, portions is the only choice.
 */
export const wastageRecipeUnits = recipe => {
  const choices = [recipePortionChoice, recipeFullChoice]
  if (!recipe) return choices
  const portionPence = wastageRecipePreviewPence(
    recipe,
    WASTAGE_COPY.portions,
    1,
  )
  const fullPence = wastageRecipePreviewPence(
    recipe,
    WASTAGE_COPY.fullRecipe,
    1,
  )
  if (portionPence != null && portionPence === fullPence) {
    return [recipePortionChoice]
  }
  return choices
}

export const wastageRecipeUnit = (last, recipe) => {
  const requested = String(last?.inputUnit || '')
    .trim()
    .toLowerCase()
  const wanted =
    requested === WASTAGE_COPY.fullRecipe.toLowerCase()
      ? WASTAGE_COPY.fullRecipe
      : WASTAGE_COPY.portions
  const offered = wastageRecipeUnits(recipe)
  if (offered.some(option => option.unit === wanted)) return wanted
  return offered[0].unit
}

/** A comma in a typed quantity is the decimal point. "1,5" is 1.5. */
export const wastageTypedQuantity = value => {
  const text = String(value ?? '')
    .trim()
    .replace(/,/g, '.')
  if (!text) return NaN
  return Number(text)
}

/** One line at or above this asks before it is saved. A yes still saves. */
export const WASTAGE_LARGE_QUANTITY = 1000

export const wastageQuantityNeedsConfirm = quantity => {
  const qty = wastageTypedQuantity(quantity)
  return Number.isFinite(qty) && qty >= WASTAGE_LARGE_QUANTITY
}

/**
 * Preview for a dish. Costs on the recipe are pounds.
 * A full recipe uses its recipe cost, otherwise portion cost times portions.
 */
export const wastageRecipePreviewPence = (recipe, unit, quantity) => {
  const qty = wastageTypedQuantity(quantity)
  if (!Number.isFinite(qty) || qty <= 0) return null
  const perPortion = Number(recipe?.costPerPortion)
  const portions = Number(recipe?.portions) > 0 ? Number(recipe.portions) : 1
  const perRecipe = Number(recipe?.costPerRecipe)
  const pounds =
    unit === WASTAGE_COPY.fullRecipe
      ? Number.isFinite(perRecipe) && perRecipe > 0
        ? perRecipe
        : Number.isFinite(perPortion) && perPortion > 0
        ? perPortion * portions
        : null
      : Number.isFinite(perPortion) && perPortion > 0
      ? perPortion
      : null
  if (pounds == null) return null
  return Math.round(qty * Math.round(pounds * 100))
}

/** Ingredient lines from the snapshot stored with the entry. */
export const wastageSnapshotIngredientLabel = ingredient => {
  const title = String(ingredient?.title || '').trim() || 'Ingredient'
  const unit = String(ingredient?.UoM || '').trim()
  const qty = ingredient?.baseQty
  const amount = [qty == null || qty === '' ? '' : String(qty), unit]
    .filter(Boolean)
    .join(' ')
  return amount ? `${title} · ${amount}` : title
}

export const wastagePreviewCostPence = (pricePounds, quantity) => {
  const qty = wastageTypedQuantity(quantity)
  const pounds = Number(pricePounds)
  if (!Number.isFinite(qty) || qty <= 0) return null
  if (!Number.isFinite(pounds) || pounds <= 0) return null
  return Math.round(qty * Math.round(pounds * 100))
}

/** "Today" on the kitchen day, otherwise "5 Oct". */
export const wastageEntryDateLabel = (businessDate, today) => {
  if (businessDate && businessDate === today) return WASTAGE_COPY.dateToday
  const parts = calendarParts(businessDate)
  if (!parts) return ''
  const todayParts = calendarParts(today)
  if (todayParts && parts.year !== todayParts.year) {
    return `${parts.day} ${parts.month} ${parts.year}`
  }
  return `${parts.day} ${parts.month}`
}

/**
 * Today's total on the Stock card.
 * An empty day is £0.00. A day whose prices are all unknown stays blank.
 * @param {number | null | undefined} costValuePence
 * @returns {string | null}
 */
export const wastageTodayCostLabel = costValuePence => {
  if (costValuePence == null || costValuePence === '') return null
  const about = wastageAboutCost(costValuePence)
  if (about) return `${about} today`
  if (Number(costValuePence) === 0) return `${formatGbp(0)} today`
  return null
}

export const pickOpenStockTakeForWastage = (stockTakes = []) =>
  (Array.isArray(stockTakes) ? stockTakes : []).find(
    stockTake => stockTake && !stockTake.isLocked,
  ) || null

/** Today is not inside the open count, so logged waste is still waiting. */
export const wastageDayWaitsOnOpenTake = (openStockTake, businessDate) => {
  if (!openStockTake || openStockTake.isLocked) return false
  const day = wastageCalendarDay(businessDate)
  const from = wastageCalendarDay(openStockTake.fromPeriod)
  const to = wastageCalendarDay(openStockTake.toPeriod)
  if (day && from && to && day >= from && day <= to) return false
  return true
}

/**
 * Oldest kitchen day with waste that is not yet on a stock take.
 */
export const pickWastageWaitingDay = (entries = []) => {
  const days = (Array.isArray(entries) ? entries : [])
    .filter(
      entry =>
        entry && !entry.deletedAt && !entry.stockTake && !entry.stockTakeId,
    )
    .map(entry => wastageCalendarDay(entry.businessDate))
    .filter(Boolean)
    .sort()
  return days[0] || null
}

/**
 * Stock list Waste card. Waiting copy names the waste day, not today.
 */
export const wastageHomeCardCostLabel = ({
  costValuePence,
  waitingDay,
} = {}) => {
  if (waitingDay) {
    const day = wastageIncludedDayLabel(waitingDay)
    return day
      ? `This waste is waiting for a stock take that includes ${day}.`
      : WASTAGE_COPY.openCountMissesDay
  }
  return wastageTodayCostLabel(costValuePence)
}

export const wastageTakeCoversDay = (stockTake, businessDate) => {
  const day = wastageCalendarDay(businessDate)
  const from = wastageCalendarDay(stockTake?.fromPeriod)
  const to = wastageCalendarDay(stockTake?.toPeriod) || from
  if (!day || !from) return false
  return day >= from && day <= to
}

/**
 * Next step for waiting waste: open a take that covers that waste day,
 * else Count today. Never send people to an unrelated supplier count.
 */
export const pickWastageWaitingNextStep = ({
  stockTakes = [],
  waitingDay,
} = {}) => {
  if (!waitingDay) return null
  const list = Array.isArray(stockTakes) ? stockTakes : []
  const coveringOpen = list.find(
    take => take && !take.isLocked && wastageTakeCoversDay(take, waitingDay),
  )
  if (coveringOpen) {
    return {
      kind: 'open',
      stockTake: coveringOpen,
      waitingDay,
      label: WASTAGE_COPY.openThisCount,
    }
  }
  const coveringAny = list.find(take => wastageTakeCoversDay(take, waitingDay))
  if (coveringAny) {
    return {
      kind: 'open',
      stockTake: coveringAny,
      waitingDay,
      label: WASTAGE_COPY.openThisCount,
    }
  }
  return {
    kind: 'create',
    stockTake: null,
    waitingDay,
    label: WASTAGE_COPY.countToday,
  }
}

/** Cost of the selected dates. An empty range is £0.00. Unknown prices stay blank. */
export const wastageFilterCostLabel = costValuePence => {
  if (costValuePence == null || costValuePence === '') return null
  if (Number(costValuePence) === 0) return formatGbp(0)
  return wastageAboutCost(costValuePence)
}

/** One portion reads “portion”. Two stay “Portions”. The stored unit is unchanged. */
export const wastageDisplayUnit = (qty, unit) => {
  const name = String(unit || '').trim()
  if (!name) return ''
  if (/^portions$/i.test(name) && Number(qty) === 1) return 'portion'
  return name
}

/** Base quantity for one selected product. Mixed or missing units stay blank. */
export const wastageFilterQuantityLabel = quantity => {
  if (!quantity || quantity.baseQty == null || quantity.baseQty === '')
    return null
  const unit = wastageDisplayUnit(quantity.baseQty, quantity.baseUoM)
  return unit ? `${quantity.baseQty} ${unit}` : String(quantity.baseQty)
}
